import { Button, notification } from 'antd';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  WarningOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import colors from 'assets/styles/colors';
import i18next from 'i18next';

import s from './styles.module.scss';

import type { ArgsProps } from 'antd/lib/notification';
import type { CSSProperties } from 'react';

type NotificationProps = ArgsProps;

const wrapperStyles: CSSProperties = {
  borderRadius: '10px',
  color: colors.WHITE,
  boxSizing: 'border-box',
  width: 500,
};

const openSuccessNotification = (config: NotificationProps): void => {
  notification.success({
    ...config,
    icon: (
      <div className={s['icon-wrapper']}>
        <CheckCircleFilled />
      </div>
    ),
    message: <div className={s['message-wrapper']}>{config.message}</div>,
    description: config.description ? (
      <div>{config.description}</div>
    ) : (
      <div className={s['empty-description']} />
    ),
    style: {
      ...wrapperStyles,
      backgroundColor: colors.RANGE_LAND,
    },
  });
};

const openInfoNotification = (config: NotificationProps): void => {
  notification.info({
    ...config,
    icon: (
      <div className={s['icon-wrapper']}>
        <InfoCircleOutlined />
      </div>
    ),
    message: <div className={s['message-wrapper']}>{config.message}</div>,
    description: config.description ? (
      <div className={s['description-wrapper']}>{config.description}</div>
    ) : (
      <div className={s['empty-description']} />
    ),
    style: {
      ...wrapperStyles,
      backgroundColor: colors.purple50,
    },
  });
};

const openWarningNotification = (config: NotificationProps): void => {
  notification.warning({
    ...config,
    icon: (
      <div className={s['icon-wrapper']}>
        <ExclamationCircleFilled />
      </div>
    ),
    message: <div className={s['message-wrapper']}>{config.message}</div>,
    description: config.description ? (
      <div>{config.description}</div>
    ) : (
      <div className={s['empty-description']} />
    ),
  });
};

const openErrorNotification = (config: NotificationProps): void => {
  let key: string | undefined;
  if (typeof config.message === 'string') {
    key = config.message.replaceAll(/[^a-zA-Z]+/g, '-');
  }

  notification.error({
    ...config,
    key,
    icon: (
      <div className={s['icon-wrapper']}>
        <WarningOutlined />
      </div>
    ),
    message: <div className={s['message-wrapper']}>{config.message}</div>,
    description: config.description ? (
      <div>{config.description}</div>
    ) : (
      <div className={s['empty-description']} />
    ),
    style: {
      ...wrapperStyles,
      backgroundColor: colors.ROCKIN_RED,
    },
    duration: config?.duration ?? 5,
  });
};

const destroy = (key: string | number) => {
  notification.destroy(key);
};

interface UndoNotificationProps extends NotificationProps {
  onUndo: () => void;
}

const openUndoNotification = (config: UndoNotificationProps): void => {
  const key = config.key || `openUndoNotification${Date.now()}`;

  notification.info({
    ...config,
    closable: false,
    pauseOnHover: false,
    showProgress: true,
    key,
    icon: (
      <div className={s['icon-wrapper']}>
        <InfoCircleOutlined />
      </div>
    ),
    message: (
      <div className={s['undo-wrapper']}>
        <div className={s['message-wrapper']}>{config.message}</div>
        <Button
          onClick={() => {
            notification.destroy(key);
            config.onUndo();
          }}
        >
          {i18next.t('controls.undo')}
        </Button>
      </div>
    ),
    description: config.description ? (
      <div>{config.description}</div>
    ) : (
      <div className={s['empty-description']} />
    ),
    style: {
      ...wrapperStyles,
      backgroundColor: colors.purple50,
    },
  });
};

export const notificationController = {
  success: openSuccessNotification,
  info: openInfoNotification,
  warning: openWarningNotification,
  error: openErrorNotification,
  destroy,
  showUndo: openUndoNotification,
};
